import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isImageRepresentationReady, toCommonMediaClientError } from '@atlaskit/media-client';
import { useCopyIntent, useFileState, useMediaClient } from '@atlaskit/media-client-react';
import { isMimeTypeSupportedByBrowser } from '@atlaskit/media-common';
import { fg } from '@atlaskit/platform-feature-flags';
import { useInteractionContext } from '@atlaskit/react-ufo/interaction-context';
import { createFailedSSRObject, extractErrorInfo } from './analytics';
import { ensureMediaFilePreviewError, ImageLoadError, MediaFilePreviewError } from './errors';
import { getAndCacheLocalPreview, getAndCacheRemotePreview, getSSRPreview, isLocalPreview, isRemotePreview, isSSRClientPreview, isSSRDataPreview, isSSRPreview, isSupportedLocalPreview, mediaFilePreviewCache } from './getPreview';
import { generateScriptProps, getSSRData } from './globalScope';
import { createRequestDimensions, isBigger, useCurrentValueRef } from './helpers';
// invisible gif for SSR preview to show the underlying spinner until the src is replaced by
// the actual image src in the inline script
var DEFAULT_SSR_PREVIEW = {
  dataURI: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
  dimensions: {
    width: 100,
    height: 100
  },
  source: 'ssr-server'
};
export var useFilePreview = function useFilePreview(_ref) {
  var _ref$resizeMode = _ref.resizeMode,
    resizeMode = _ref$resizeMode === void 0 ? 'crop' : _ref$resizeMode,
    identifier = _ref.identifier,
    ssr = _ref.ssr,
    dimensions = _ref.dimensions,
    _ref$useSrcSet = _ref.useSrcSet,
    useSrcSet = _ref$useSrcSet === void 0 ? false : _ref$useSrcSet,
    traceContext = _ref.traceContext,
    skipRemote = _ref.skipRemote,
    mediaBlobUrlAttrs = _ref.mediaBlobUrlAttrs,
    _ref$allowAnimated = _ref.allowAnimated,
    allowAnimated = _ref$allowAnimated === void 0 ? true : _ref$allowAnimated,
    upscale = _ref.upscale,
    maxAge = _ref.maxAge,
    source = _ref.source;
  var mediaClient = useMediaClient();
  var _useState = useState('loading'),
    _useState2 = _slicedToArray(_useState, 2),
    status = _useState2[0],
    setStatus = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    nonCriticalError = _useState6[0],
    setNonCriticalError = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isBannedLocalPreview = _useState8[0],
    setIsBannedLocalPreview = _useState8[1];
  var _useState9 = useState('not-resolved'),
    _useState0 = _slicedToArray(_useState9, 2),
    upfrontPreviewStatus = _useState0[0],
    setUpfrontPreviewStatus = _useState0[1];
  var ssrReliabilityRef = useRef({
    server: {
      status: 'unknown'
    },
    client: {
      status: 'unknown'
    }
  });
  var ufoContext = useInteractionContext();
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isLoading = _useState10[0],
    setIsLoading = _useState10[1];
  useLayoutEffect(function () {
    if (isLoading && fg('platform_close_image_blindspot_2')) {
      return ufoContext === null || ufoContext === void 0 ? void 0 : ufoContext.hold('img-loading');
    }
  }, [ufoContext, isLoading]);
  var requestDimensions = useMemo(function () {
    return dimensions ? createRequestDimensions(dimensions) : undefined;
  }, [dimensions]);
  var requestDimensionsRef = useCurrentValueRef(requestDimensions);
  var imageURLParams = _objectSpread(_objectSpread({
    collection: identifier.collectionName,
    mode: resizeMode
  }, requestDimensions), {}, {
    allowAnimated: allowAnimated,
    upscale: upscale,
    'max-age': maxAge
  });
  imageURLParams = _objectSpread(_objectSpread({}, imageURLParams), {}, {
    source: source
  });
  var previewInitializer = function previewInitializer() {
    var preview = mediaFilePreviewCache.get(identifier.id, resizeMode);
    if (preview) {
      return preview;
    }
    if (ssr) {
      var ssrData = getSSRData(identifier, resizeMode);
      if (ssrData !== null && ssrData !== void 0 && ssrData.error) {
        ssrReliabilityRef.current.server = _objectSpread({
          status: 'fail'
        }, ssrData.error);
      }
      if (!(ssrData !== null && ssrData !== void 0 && ssrData.dataURI)) {
        try {
          return getSSRPreview(ssr, mediaClient, identifier.id, imageURLParams, mediaBlobUrlAttrs);
        } catch (e) {
          ssrReliabilityRef.current[ssr] = _objectSpread({
            status: 'fail'
          }, extractErrorInfo(e, traceContext));
        }
      } else {
        var _dimensions = ssrData.dimensions,
          dataURI = ssrData.dataURI,
          srcSet = ssrData.srcSet;
        return {
          dataURI: dataURI,
          dimensions: _dimensions,
          source: 'ssr-data',
          srcSet: srcSet
        };
      }
    }
  };
  var _useState11 = useState(previewInitializer),
    _useState12 = _slicedToArray(_useState11, 2),
    preview = _useState12[0],
    setPreview = _useState12[1];

  //----------------------------------------------------------------
  // FILE STATE
  //----------------------------------------------------------------

  var _useFileState = useFileState(identifier.id, {
      skipRemote: skipRemote,
      collectionName: identifier.collectionName,
      occurrenceKey: identifier.occurrenceKey
    }),
    fileState = _useFileState.fileState;

  // Derived from File State
  var isBackendPreviewReady = !!fileState && isImageRepresentationReady(fileState);
  var fileStateErrorMessage = (fileState === null || fileState === void 0 ? void 0 : fileState.status) === 'error' ? fileState.message : undefined;
  var _ref2 = fileState && (fileState === null || fileState === void 0 ? void 0 : fileState.status) !== 'error' ? fileState : {},
    _ref2$preview = _ref2.preview,
    localBinary = _ref2$preview === void 0 ? undefined : _ref2$preview,
    _ref2$mediaType = _ref2.mediaType,
    mediaType = _ref2$mediaType === void 0 ? undefined : _ref2$mediaType,
    _ref2$mimeType = _ref2.mimeType,
    mimeType = _ref2$mimeType === void 0 ? undefined : _ref2$mimeType;

  //----------------------------------------------------------------
  // Update status
  //----------------------------------------------------------------

  // TOOD: make a full hook reset (remount) on New identifier or client
  useEffect(function () {
    setStatus('loading');
  }, [identifier]);
  useEffect(function () {
    if (status !== 'error') {
      if (preview || (fileState === null || fileState === void 0 ? void 0 : fileState.status) === 'processed' && !isBackendPreviewReady) {
        setStatus('complete');
      } else if (!preview && (fileState === null || fileState === void 0 ? void 0 : fileState.status) === 'failed-processing' && !isBackendPreviewReady) {
        setStatus('error');
        setError(new MediaFilePreviewError('failed-processing'));
      } else if (!preview && (fileState === null || fileState === void 0 ? void 0 : fileState.status) === 'error' && upfrontPreviewStatus === 'resolved') {
        setStatus('error');
        setError(new MediaFilePreviewError('metadata-fetch', toCommonMediaClientError(fileState)));
      } else {
        setStatus('loading');
      }
    }
  }, [preview, status, fileState, isBackendPreviewReady, fileStateErrorMessage, upfrontPreviewStatus]);

  //----------------------------------------------------------------
  // SSR Loading
  //----------------------------------------------------------------

  useEffect(function () {
    var _getSSRData;
    if (!fg('media-perf-uplift-mutation-fix')) {
      return;
    }
    var loadPromise = (_getSSRData = getSSRData(identifier, resizeMode)) === null || _getSSRData === void 0 ? void 0 : _getSSRData.loadPromise;
    if (preview && isSSRDataPreview(preview) && loadPromise) {
      loadPromise.then(function () {
        setStatus('complete');
      }).catch(function () {
        setPreview(undefined);
      });
    }
  }, [preview, identifier, resizeMode]);

  //----------------------------------------------------------------
  // Preview Fetch Helper
  //----------------------------------------------------------------
  var getAndCacheRemotePreviewRef = useCurrentValueRef(function () {
    return getAndCacheRemotePreview(mediaClient, identifier.id, requestDimensions || {}, imageURLParams, mediaBlobUrlAttrs, traceContext);
  });

  //----------------------------------------------------------------
  // Upfront Preview
  //----------------------------------------------------------------
  useEffect(function () {
    // Only fetch upfront (no file state) if there is no preview in the state already
    if (preview) {
      setUpfrontPreviewStatus('resolved');
    } else if (!preview && upfrontPreviewStatus === 'not-resolved' && !skipRemote) {
      // We block any possible future call to this method regardless of the outcome (success or fail)
      // If it fails, the normal preview fetch should occur after the file state is fetched anyways
      setUpfrontPreviewStatus('resolving');
      setIsLoading(true);
      var fetchedDimensions = _objectSpread({}, requestDimensions);
      getAndCacheRemotePreviewRef.current().then(function (newPreview) {
        // If there are new and bigger dimensions in the props, and the upfront preview is still resolving,
        // the fetched preview is no longer valid, and thus, we dismiss it
        if (!isBigger(fetchedDimensions, requestDimensionsRef.current)) {
          setPreview(newPreview);
        }
      }).catch(function () {
        // NO need to log error. If this call fails, a refetch will happen after
      }).finally(function () {
        setIsLoading(false);
        setUpfrontPreviewStatus('resolved');
      });
    }
  }, [getAndCacheRemotePreviewRef, preview, requestDimensions, requestDimensionsRef, skipRemote, upfrontPreviewStatus]);

  //----------------------------------------------------------------
  // Cache, Local & Remote Preview
  //----------------------------------------------------------------

  var mediaBlobUrlAttrsRef = useCurrentValueRef(mediaBlobUrlAttrs);
  useEffect(function () {
    var cachedPreview = mediaFilePreviewCache.get(identifier.id, resizeMode);

    // Cached Preview ----------------------------------------------------------------
    if (!preview && cachedPreview && !isBigger(cachedPreview === null || cachedPreview === void 0 ? void 0 : cachedPreview.dimensions, requestDimensions)) {
      setPreview(cachedPreview);
    }
    // Local Preview ----------------------------------------------------------------
    else if (!preview && !isBannedLocalPreview && localBinary && isSupportedLocalPreview(mediaType) && isMimeTypeSupportedByBrowser(mimeType || '')) {
      // Local preview is available only if it's supported by browser and supported by Media Card (isSupportedLocalPreview)
      // For example, SVGs are mime type NOT supported by browser but media type supported by Media Card (image)
      // Then, local Preview NOT available
      setIsLoading(true);
      getAndCacheLocalPreview(identifier.id, localBinary, requestDimensions || {}, resizeMode, mediaBlobUrlAttrsRef.current).then(setPreview).catch(function (e) {
        setIsBannedLocalPreview(true);
        // CXP-2723 TODO: We might have to wrap this error in MediaCardError
        setNonCriticalError(e);
      }).finally(function () {
        setIsLoading(false);
      });
    }
    // Remote Preview ----------------------------------------------------------------
    else if (!error && !nonCriticalError && (!preview || isBigger(preview.dimensions, requestDimensions) ||
    // We always refetch SSR preview to be able to browser-cache a version without the token in the query parameters
    isSSRPreview(preview)) && !skipRemote && upfrontPreviewStatus === 'resolved' && isBackendPreviewReady) {
      // If the preview is SSR, we can skip the refetch as it will be handled by the global scope promise
      if (preview && isSSRPreview(preview) && fg('media-perf-uplift-mutation-fix')) {
        return;
      }
      setIsLoading(true);
      getAndCacheRemotePreviewRef.current().then(setPreview).catch(function (e) {
        var wrappedError = ensureMediaFilePreviewError('preview-fetch', e);
        if (!preview) {
          setStatus('error');
          setError(wrappedError);
        } else {
          // If there is already a preview, we consider it a non-critical error
          setNonCriticalError(wrappedError);
        }
      }).finally(function () {
        setIsLoading(false);
      });
    }
  }, [error, nonCriticalError, getAndCacheRemotePreviewRef, identifier.id, resizeMode, isBannedLocalPreview, mediaBlobUrlAttrsRef, preview, requestDimensions, skipRemote, isBackendPreviewReady, localBinary, mediaType, mimeType, upfrontPreviewStatus]);

  //----------------------------------------------------------------
  // RETURN
  //----------------------------------------------------------------

  var onImageError = useCallback(function (failedPreview) {
    if (!failedPreview) {
      return;
    }
    if (isSSRClientPreview(failedPreview)) {
      ssrReliabilityRef.current.client = createFailedSSRObject(failedPreview, traceContext);
    }
    var isSSR = isSSRDataPreview(failedPreview) || (fg('jfp-magma-fix-failed-ssr-preview-load') ? isSSRClientPreview(failedPreview) : false);

    // If the preview failed and it comes from server (global scope / ssrData), it means that we have reused it in client and the error counts for both: server & client.
    if (isSSR) {
      ssrReliabilityRef.current.server = createFailedSSRObject(failedPreview, traceContext);
      ssrReliabilityRef.current.client = createFailedSSRObject(failedPreview, traceContext);
    }

    // If the dataURI has been replaced, we can dismiss this error
    if (failedPreview.dataURI !== (preview === null || preview === void 0 ? void 0 : preview.dataURI)) {
      return;
    }
    var isLocal = isLocalPreview(failedPreview);
    var isRemote = isRemotePreview(failedPreview);
    if (isLocal || isRemote || isSSR) {
      var _error = new ImageLoadError(failedPreview === null || failedPreview === void 0 ? void 0 : failedPreview.source);
      mediaFilePreviewCache.remove(identifier.id, resizeMode);
      if (isLocal) {
        setIsBannedLocalPreview(true);
      }
      if (isRemote) {
        setStatus('error');
        setError(_error);
      }
      setPreview(undefined);
    }
  }, [identifier.id, preview === null || preview === void 0 ? void 0 : preview.dataURI, resizeMode, traceContext]);
  var onImageLoad = useCallback(function (newPreview) {
    if (newPreview) {
      if (isSSRClientPreview(newPreview) && ssrReliabilityRef.current.client.status === 'unknown') {
        ssrReliabilityRef.current.client = {
          status: 'success'
        };
      }

      /*
          If the image loads successfully and it comes from server (global scope / ssrData), it means that we have reused it in client and the success counts for both: server & client.
        */

      if (isSSRDataPreview(newPreview) && ssrReliabilityRef.current.server.status === 'unknown') {
        ssrReliabilityRef.current.server = {
          status: 'success'
        };
        ssrReliabilityRef.current.client = {
          status: 'success'
        };
      }
    }

    // If the dataURI has been replaced, we can dismiss this callback
    if ((newPreview === null || newPreview === void 0 ? void 0 : newPreview.dataURI) !== (preview === null || preview === void 0 ? void 0 : preview.dataURI)) {
      return;
    }
  }, [preview === null || preview === void 0 ? void 0 : preview.dataURI]);

  // FOR SSR
  var getSsrScriptProps = ssr === 'server' ? function () {
    var _ssrReliabilityRef$cu;
    return generateScriptProps(identifier, preview === null || preview === void 0 ? void 0 : preview.dataURI, resizeMode, preview === null || preview === void 0 ? void 0 : preview.srcSet, requestDimensions, ((_ssrReliabilityRef$cu = ssrReliabilityRef.current.server) === null || _ssrReliabilityRef$cu === void 0 ? void 0 : _ssrReliabilityRef$cu.status) === 'fail' ? ssrReliabilityRef.current.server : undefined, {
      'media-perf-uplift-mutation-fix': fg('media-perf-uplift-mutation-fix')
    });
  } : undefined;
  var _useCopyIntent = useCopyIntent(identifier.id, {
      collectionName: identifier.collectionName
    }),
    copyNodeRef = _useCopyIntent.copyNodeRef;

  // CXP-2723 TODO: should consider simplifying our analytics, and how
  // we might get rid of ssrReliabiltyRef from our hook
  return {
    preview: ssr === 'server' && useSrcSet && fg('media-perf-uplift-mutation-fix') ? DEFAULT_SSR_PREVIEW : preview,
    status: status,
    error: error,
    nonCriticalError: nonCriticalError,
    ssrReliability: ssrReliabilityRef.current,
    onImageError: onImageError,
    onImageLoad: onImageLoad,
    getSsrScriptProps: getSsrScriptProps,
    copyNodeRef: copyNodeRef
  };
};